import clsx, { ClassValue } from 'clsx';
import { isNil } from 'lodash';
import { CSSProperties, ReactNode } from 'react';
import { CssVars } from '@shared/components/View';
import styles from './Text.module.css';

export type TextAlign = 'left' | 'center' | 'right';

export type TextSize =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'subtitle'
  | 'body1'
  | 'body2'
  | 'overline'
  | 'caption1'
  | 'caption2';

export type TextWeight = 'light' | 'normal' | 'medium' | 'semibold' | 'bold';

export interface TextProps {
  children?: ReactNode;
  size?: TextSize;
  weight?: TextWeight;
  align?: TextAlign;
  monospace?: boolean;
  italic?: boolean;
  numberOfLines?: number;
  balance?: boolean;
  pretty?: boolean;
  color?: string;
  accent?: boolean;
  inactive?: boolean;
  danger?: boolean;
  lineThrough?: boolean;
  className?: ClassValue;
  style?: CSSProperties;
  testID?: string;
  onClick?: VoidFunction;
  selectable?: boolean;
  block?: boolean;
  flex?: boolean;
}

export function Text({
  children,
  size,
  weight,
  monospace,
  italic,
  align,
  numberOfLines,
  balance,
  pretty,
  color,
  accent,
  inactive,
  danger,
  lineThrough,
  className,
  style,
  testID,
  onClick,
  selectable = true,
  block = !isNil(align),
  flex = false,
  ...rest
}: TextProps) {
  const vars = createStyleVars({ 'font-color': color, numberOfLines });
  return (
    <span
      className={clsx([styles.text, flex && styles.flex, className])}
      style={{ ...style, ...vars }}
      data-size={size}
      data-weight={weight}
      data-align={align}
      data-lines={numberOfLines}
      data-balance={balance || undefined}
      data-pretty={pretty || undefined}
      data-monospace={monospace || undefined}
      data-italic={italic || undefined}
      data-block={block || undefined}
      data-accent={accent || undefined}
      data-inactive={inactive || undefined}
      data-danger={danger || undefined}
      data-line-through={lineThrough || undefined}
      data-selectable={selectable || undefined}
      data-clickable={onClick ? true : undefined}
      data-testid={testID}
      onClick={onClick}
      {...rest}
    >
      {children}
    </span>
  );
}

function createStyleVars(vars: CssVars = {}) {
  return Object.entries(vars).reduce((memo: CssVars, [key, value]) => {
    if (isNil(value)) {
      return memo;
    } else {
      return { ...memo, [`--${key}`]: value };
    }
  }, {});
}
